import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import LZString from 'lz-string';
import { Loader2, Heart, Star, CheckCircle2, AlertTriangle, ShieldAlert, Sparkles, Calendar, Clock } from 'lucide-react';
import { logger } from '../utils/logger';
import { API_URL } from '../utils/api';
import { useTitreOnglet } from '../hooks/useTitreOnglet';

export default function SharedAdvicePage() {
  useTitreOnglet('Fiche conseils');
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [adviceData, setAdviceData] = useState(null);

  // Decode advice from URL fragment (legacy fallback)
  const decodedFromHash = useMemo(() => {
    try {
      const hash = window.location.hash || '';
      const match = hash.match(/^#d=(.+)$/);
      if (!match) return null;
      const json = LZString.decompressFromEncodedURIComponent(match[1]);
      if (!json) return null;
      const obj = JSON.parse(json);
      return { advice: obj.a || '', doctorName: obj.n || '', doctorMessage: obj.m || '' };
    } catch (err) {
      logger.warn('SharedAdvice: LZ decode failed:', err?.message);
      return null;
    }
  }, []);

  const decoded = adviceData || decodedFromHash;

  // Parse structured advice JSON
  const parsed = useMemo(() => {
    if (!decoded?.advice) return null;
    try {
      const obj = typeof decoded.advice === 'string' ? JSON.parse(decoded.advice) : decoded.advice;
      if (obj?.sections) return obj;
    } catch (err) { logger.warn('SharedAdvice: JSON parse failed:', err?.message); }
    return null;
  }, [decoded]);

  useEffect(() => {
    const checkToken = async () => {
      try {
        const { data: res } = await axios.get(`${API_URL}/api/advice/view/${token}`);
        if (res.rating) { setRating(res.rating); setRatingSubmitted(true); }

        // Decode advice data from server response (new short URL flow)
        if (res.encoded_advice && !decodedFromHash) {
          try {
            const json = LZString.decompressFromEncodedURIComponent(res.encoded_advice);
            if (json) {
              const obj = JSON.parse(json);
              setAdviceData({ advice: obj.a || '', doctorName: obj.n || '', doctorMessage: obj.m || '' });
            }
          } catch (decErr) {
            logger.warn('SharedAdvice: server data decode failed:', decErr?.message);
          }
        }
      } catch (err) {
        const status = err.response?.status;
        if (status === 404) setError('Ce lien est introuvable.');
        else if (status === 410) setError('Ce lien a expire (validite 24h).');
        else setError('Erreur de chargement.');
      }
      setLoading(false);
    };
    checkToken();
  }, [token, decodedFromHash]);

  const handleRate = async () => {
    if (!rating || ratingSubmitted) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/advice/view/${token}/rate`, { rating, comment });
      setRatingSubmitted(true);
    } catch (err) { logger.warn('SharedAdvice: rating submit failed:', err?.message); }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-teal-500 animate-spin mx-auto mb-3" />
          <p className="text-sm text-teal-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
            <Clock className="w-7 h-7 text-red-400" />
          </div>
          <h1 className="text-xl font-semibold text-slate-800 mb-2 font-['Outfit']">{error}</h1>
          <p className="text-sm text-slate-500">Demandez un nouveau lien a votre medecin.</p>
        </div>
      </div>
    );
  }

  const doctorName = decoded?.doctorName || 'Votre medecin';
  const doctorMessage = decoded?.doctorMessage;

  const sectionCfg = {
    know: { label: 'A savoir', icon: <Sparkles className="w-5 h-5" />, gradient: 'from-sky-50 to-blue-50', border: 'border-sky-200/60', iconBg: 'bg-sky-100', iconColor: 'text-sky-600', dot: 'bg-sky-400', labelColor: 'text-sky-700' },
    do: { label: 'Les bons reflexes', icon: <CheckCircle2 className="w-5 h-5" />, gradient: 'from-emerald-50 to-green-50', border: 'border-emerald-200/60', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600', dot: 'bg-emerald-400', labelColor: 'text-emerald-700' },
    dont: { label: 'A eviter', icon: <AlertTriangle className="w-5 h-5" />, gradient: 'from-amber-50 to-orange-50', border: 'border-amber-200/60', iconBg: 'bg-amber-100', iconColor: 'text-amber-600', dot: 'bg-amber-500', labelColor: 'text-amber-700' },
    alert: { label: 'Consultez si...', icon: <ShieldAlert className="w-5 h-5" />, gradient: 'from-red-50 to-rose-50', border: 'border-red-200/60', iconBg: 'bg-red-100', iconColor: 'text-red-600', dot: 'bg-red-400', labelColor: 'text-red-600' },
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50/80 via-white to-slate-50">
      <div className="h-1 bg-gradient-to-r from-teal-400 via-emerald-400 to-cyan-400" />

      <main className="max-w-lg mx-auto px-4 py-6 sm:py-10">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-teal-400 to-emerald-500 shadow-lg shadow-teal-200/50 mb-4">
            <Heart className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-lg font-semibold text-slate-800 font-['Outfit'] mb-1">Vos conseils sante</h1>
          <p className="text-sm text-slate-500">
            De la part de <span className="font-medium text-teal-700">{doctorName}</span>
          </p>
        </div>

        {doctorMessage && (
          <div className="mb-5 p-4 bg-white rounded-2xl border border-teal-100 shadow-sm">
            <p className="text-sm text-slate-600 italic leading-relaxed">"{doctorMessage}"</p>
            <p className="text-xs text-teal-600 mt-2 font-medium">{doctorName}</p>
          </div>
        )}

        {parsed ? (
          <div className="space-y-5">
            {parsed.sections.map((section, si) => (
              <div key={section.title || `section-${si}`} className="space-y-3">
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                  <div className="px-5 py-4 bg-gradient-to-r from-teal-50 to-emerald-50 border-b border-teal-100/40">
                    <h2 className="text-lg font-semibold text-slate-800 font-['Outfit'] leading-snug">{section.title}</h2>
                    <p className="text-sm text-slate-500 mt-1 leading-relaxed">{section.summary}</p>
                  </div>
                  <div className="p-4 space-y-3">
                    {['know', 'do', 'dont', 'alert'].map(key => {
                      const items = section[key];
                      if (!items?.length) return null;
                      const cfg = sectionCfg[key];
                      return (
                        <div key={key} className={`bg-gradient-to-br ${cfg.gradient} border ${cfg.border} rounded-xl p-4`}>
                          <div className="flex items-center gap-2.5 mb-3">
                            <div className={`w-8 h-8 rounded-lg ${cfg.iconBg} flex items-center justify-center ${cfg.iconColor}`}>
                              {cfg.icon}
                            </div>
                            <span className={`text-sm font-bold ${cfg.labelColor} uppercase tracking-wide`}>{cfg.label}</span>
                          </div>
                          <ul className="space-y-2.5">
                            {items.map((item, ii) => (
                              <li key={`${key}-${ii}-${item.slice(0,20)}`} className="flex items-start gap-2.5">
                                <div className={`w-2 h-2 rounded-full ${cfg.dot} mt-1.5 flex-shrink-0`} />
                                <span className="text-sm text-slate-700 leading-relaxed">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
            {parsed.followup && (
              <div className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-indigo-100 shadow-sm">
                <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-indigo-500" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-0.5">Votre suivi</p>
                  <p className="text-sm text-slate-700 leading-relaxed">{parsed.followup}</p>
                </div>
              </div>
            )}
          </div>
        ) : decoded?.advice ? (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <div className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
              {decoded.advice}
            </div>
          </div>
        ) : null}

        <div className="mt-8 bg-white rounded-2xl border border-slate-100 shadow-sm p-5 text-center">
          <p className="text-sm font-medium text-slate-700 mb-3">
            {ratingSubmitted ? 'Merci pour votre retour !' : 'Cette fiche vous a-t-elle ete utile ?'}
          </p>
          <div className="flex justify-center gap-1.5 mb-3">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => !ratingSubmitted && setRating(star)}
                disabled={ratingSubmitted}
                className={`transition-all duration-150 ${ratingSubmitted ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
                data-testid={`advice-rating-star-${star}`}
              >
                <Star
                  className={`w-8 h-8 ${star <= rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`}
                />
              </button>
            ))}
          </div>
          {!ratingSubmitted && rating > 0 && (
            <div className="space-y-2.5">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Un commentaire ? (optionnel)"
                rows={2}
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-teal-300 placeholder:text-slate-400"
                data-testid="advice-rating-comment"
              />
              <button
                onClick={handleRate}
                disabled={submitting}
                className="w-full h-10 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-sm font-medium hover:from-teal-600 hover:to-emerald-600 transition-all disabled:opacity-50"
                data-testid="advice-rating-submit"
              >
                {submitting ? 'Envoi...' : 'Envoyer'}
              </button>
            </div>
          )}
        </div>

        <div className="mt-6 text-center">
          <p className="text-[11px] text-slate-400">
            Fiche partagée par {doctorName}
          </p>
          <p className="text-[10px] text-slate-300 mt-1">
            Ces conseils ne remplacent pas une consultation medicale
          </p>
        </div>
      </main>
    </div>
  );
}
